/**
 * QA Service - Agent quality scoring API calls
 */

import { createApiClient } from '@shared/auth';
import apiConfig from '../config/apiConfig';
import { extractErrorMessage } from '../utils/responseHelpers';

const client = createApiClient(apiConfig.getQAServiceUrl());

/**
 * Get QA analytics summary
 */
export const getQASummary = async (dateRange) => {
  try {
    const response = await client.get('/api/v1/analytics/summary', { params: dateRange });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch QA summary.') };
  }
};

/**
 * Get per-dimension quality averages
 */
export const getQADimensions = async (dateRange) => {
  try {
    const response = await client.get('/api/v1/analytics/dimensions', { params: dateRange });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch dimensions.') };
  }
};

/**
 * Get QA scores list with pagination
 */
export const getQAScores = async (params) => {
  try {
    const response = await client.get('/api/v1/scores', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch QA scores.') };
  }
};

/**
 * Get single QA score by interaction ID
 */
export const getQAScore = async (interactionId) => {
  try {
    const response = await client.get(`/api/v1/scores/${interactionId}`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch score.') };
  }
};

/**
 * Dispute a QA score
 */
export const disputeScore = async (interactionId, reason) => {
  try {
    const response = await client.post(`/api/v1/scores/${interactionId}/dispute`, { reason });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to submit dispute.') };
  }
};

/**
 * Get disputes list
 */
export const getDisputes = async (params) => {
  try {
    const response = await client.get('/api/v1/disputes', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch disputes.') };
  }
};

/**
 * Get per-sub-parameter average scores
 */
export const getSubParameterAnalytics = async (dateRange) => {
  try {
    const response = await client.get('/api/v1/analytics/sub-parameters', { params: dateRange });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch sub-parameter analytics.') };
  }
};

/**
 * Resolve a dispute (manager action)
 */
export const resolveDispute = async (disputeId, data) => {
  try {
    const response = await client.put(`/api/v1/disputes/${disputeId}/resolve`, data);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to resolve dispute.') };
  }
};

const qaServiceDefault = {
  getQASummary,
  getQADimensions,
  getQAScores,
  getQAScore,
  getSubParameterAnalytics,
  disputeScore,
  getDisputes,
  resolveDispute,
};

export default qaServiceDefault;
